import React, { useState, useCallback } from "react";
import logoAssets from "../data/logoAssets";
import { getAuth } from "firebase/auth";
import ConfirmModal from "./ConfirmModal";
import { useNotifications } from "../hooks/notificationsContext";

export default function CompanyLogoEditor({ companyDoc }) {
  const [accent, setAccent] = useState(companyDoc.companyLogoAccent ?? 0);
  const [accentColor, setAccentColor] = useState(companyDoc.companyLogoAccentColor ?? 0);
  const [base, setBase] = useState(companyDoc.companyLogoBase ?? 0);
  const [baseColor, setBaseColor] = useState(companyDoc.companyLogoBaseColor ?? 0);
  const [symbol, setSymbol] = useState(companyDoc.companyLogoSymbol ?? 0);
  const [symbolColor, setSymbolColor] = useState(companyDoc.companyLogoSymbolColor ?? 0);
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const { addMessage } = useNotifications();

  const THEME_COLOR = "#50E3C2";
  const colorPalette = {
    0: "#50E3C2", 1: "#7FF1D5", 2: "#A8F7E2", 3: "#D0FCF0", 4: "#2BAF95",
    5: "#0A1F1F", 6: "#F4C542", 7: "#FF5E5E", 8: "#7ED957", 9: "#FFA552",
    10: "#F8F9FA", 11: "#C6CED3", 12: "#899CA3", 13: "#4A555A", 14: "#1B1F1F",
  };

  const handleReset = () => {
    setAccent(companyDoc.companyLogoAccent ?? 0);
    setAccentColor(companyDoc.companyLogoAccentColor ?? 0);
    setBase(companyDoc.companyLogoBase ?? 0);
    setBaseColor(companyDoc.companyLogoBaseColor ?? 0);
    setSymbol(companyDoc.companyLogoSymbol ?? 0);
    setSymbolColor(companyDoc.companyLogoSymbolColor ?? 0);
  };

  const getAssetSrc = (items, index) => {
    const arr = Array.isArray(items) ? items : Object.values(items || {});
    return arr[index] || null;
  };

  const TintedLayer = ({ src, color }) =>
    src ? (
      <div
        style={{
          position: "absolute",
          inset: 0,
          WebkitMaskImage: `url(${src})`,
          maskImage: `url(${src})`,
          WebkitMaskRepeat: "no-repeat",
          maskRepeat: "no-repeat",
          WebkitMaskSize: "contain",
          maskSize: "contain",
          backgroundColor: colorPalette[color] || "transparent",
        }}
      />
    ) : null;

  const handleSubmit = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getAuth().currentUser.getIdToken();
      const payload = {
        accent: Number(accent),
        accentColor: Number(accentColor),
        base: Number(base),
        baseColor: Number(baseColor),
        symbol: Number(symbol),
        symbolColor: Number(symbolColor),
      };

      const res = await fetch(
        "https://us-central1-stockmarket-83a73.cloudfunctions.net/companyChangeLogo",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) throw new Error("Failed to update logo");
      addMessage("Company logo updated successfully.", "success");
    } catch (err) {
      addMessage("Failed to update logo.", "error");
    } finally {
      setLoading(false);
    }
  }, [accent, accentColor, base, baseColor, symbol, symbolColor, addMessage]);

  const renderCarousel = (items, selected, setSelected, color, setColor) => {
    const itemsArray = Array.isArray(items) ? items : Object.values(items || {});
    
    return (
      <div style={{ marginBottom: 16 }}>
        <div
          className="custom-scrollbar"
          style={{
            display: "flex",
            overflowX: "auto",
            gap: 8,
            padding: "10px 4px",
            border: "1px solid rgba(80, 227, 194, 0.2)",
            borderRadius: 6,
            background: "rgba(0, 0, 0, 0.4)",
            whiteSpace: "nowrap"
          }}
        >
          {itemsArray.map((src, id) => (
            <div
              key={id}
              onClick={() => setSelected(id)}
              style={{
                minWidth: 48,
                height: 48,
                border: selected === id ? `2px solid ${colorPalette[0]}` : "2px solid transparent",
                borderRadius: 4,
                background: "#111",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                flexShrink: 0,
                position: 'relative'
              }}
            >
              <img 
                src={src} 
                alt="" 
                style={{ 
                  maxWidth: "85%", 
                  maxHeight: "85%",
                  display: id === 0 ? "none" : "block"
                }} 
              />
            </div>
          ))}
        </div>
        
        <div style={{ display: "flex", gap: 4, justifyContent: "center", marginTop: 10 }}>
          {Object.entries(colorPalette).map(([key, hex]) => (
            <div
              key={key}
              onClick={() => setColor(parseInt(key))}
              style={{
                width: 14,
                height: 14,
                borderRadius: "50%",
                background: hex,
                border: color === parseInt(key) ? "2px solid white" : "1px solid rgba(255,255,255,0.1)",
                cursor: "pointer",
              }}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div style={{ textAlign: "center", color: "#fff", fontFamily: "monospace" }}>
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
          height: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.2);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: ${THEME_COLOR}66; 
          border-radius: 10px;
          border: 1px solid rgba(0, 0, 0, 0.1);
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: ${THEME_COLOR};
          box-shadow: 0 0 8px ${THEME_COLOR}44;
        }
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: ${THEME_COLOR}66 rgba(0, 0, 0, 0.2);
        }
      `}</style>

      <h2 style={{ fontSize: "0.9rem", marginBottom: 12, textTransform: "uppercase", letterSpacing: "1px" }}>
        Logo Configuration
      </h2>

      <div
        style={{
          position: "relative",
          width: 80,
          height: 80,
          margin: "0 auto 16px auto",
          background: "rgba(80, 227, 194, 0.05)",
          border: `1px solid ${THEME_COLOR}33`,
          borderRadius: 8,
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <div style={{ position: 'relative', width: 64, height: 64 }}>
          <TintedLayer src={getAssetSrc(logoAssets.baseShapes, base)} color={baseColor} />
          <TintedLayer src={getAssetSrc(logoAssets.accents, accent)} color={accentColor} />
          <TintedLayer src={getAssetSrc(logoAssets.symbols, symbol)} color={symbolColor} />
        </div>
      </div>

      <div style={{ padding: "0 10px" }}>
        {renderCarousel(logoAssets.baseShapes, base, setBase, baseColor, setBaseColor)}
        {renderCarousel(logoAssets.accents, accent, setAccent, accentColor, setAccentColor)}
        {renderCarousel(logoAssets.symbols, symbol, setSymbol, symbolColor, setSymbolColor)}
      </div>

      <div style={{ marginTop: 16, display: "flex", gap: 12, justifyContent: "center" }}>
  
        <button 
          onClick={handleReset}
          style={{
            padding: "8px 24px",
            background: "transparent",
            border: `1px solid #ff5e5e`,
            color: "#ff5e5e",
            cursor: "pointer",
            textTransform: "uppercase",
            fontSize: "10px",
            letterSpacing: "2px",
            opacity: loading ? 0.5 : 1
          }}
          disabled={loading}
        >
          Reset
        </button>

        <button 
          className="settings-button" 
          onClick={() => setShowConfirm(true)} 
          disabled={loading}
          style={{
            padding: "8px 24px",
            background: "transparent",
            border: `1px solid ${THEME_COLOR}`,
            color: THEME_COLOR,
            cursor: "pointer",
            textTransform: "uppercase",
            fontSize: "10px",
            letterSpacing: "2px"
          }}
        >
          {loading ? "Updating..." : "Update Logo"}
        </button>
      </div>

      {showConfirm && (
        <ConfirmModal
          title="Confirm Update"
          message="Apply this logo to your company?"
          onConfirm={() => { setShowConfirm(false); handleSubmit(); }}
          onCancel={() => setShowConfirm(false)}
        />
      )}
    </div>
  );
}